import { Information } from "../../simulation/types"
import { resolveTreeNodeId } from "../utils/tree-resolver"
import { TutorialConfig } from "../types/tutorial-config"
import { isKnownStableSelector } from "./selector-catalog"

export type ValidationIssue = {
  level: "error" | "warning"
  scope:
    | { kind: "step"; stepId: string; field?: string }
    | { kind: "message"; messageId: string; field?: string }
    | { kind: "mapping"; mappingName: string }
    | { kind: "trigger"; index: number; collection: "search" | "tree" }
    | { kind: "global"; field?: string }
  message: string
}

const VARIABLE_PATTERN = /{{\s*([a-zA-Z0-9_-]+)\s*}}/g

const collectVariables = (text: string | string[]): string[] => {
  const matches: string[] = []
  const sources = Array.isArray(text) ? text : [text]
  for (const src of sources) {
    if (!src) continue
    let m: RegExpExecArray | null
    const re = new RegExp(VARIABLE_PATTERN.source, VARIABLE_PATTERN.flags)
    while ((m = re.exec(src)) !== null) {
      matches.push(m[1])
    }
  }
  return matches
}

export const validateConfig = (
  config: TutorialConfig,
  categories: Information[],
): ValidationIssue[] => {
  const issues: ValidationIssue[] = []

  // 1. Step IDs unique
  const stepIds = new Set<string>()
  for (const step of config.steps) {
    if (!step.id) {
      issues.push({
        level: "error",
        scope: { kind: "step", stepId: step.id || "(blank)", field: "id" },
        message: "Step ID is required",
      })
      continue
    }
    if (stepIds.has(step.id)) {
      issues.push({
        level: "error",
        scope: { kind: "step", stepId: step.id, field: "id" },
        message: `Duplicate step id "${step.id}"`,
      })
    }
    stepIds.add(step.id)
  }

  // 2. Variable references resolve
  const knownVarNames = new Set([
    ...Object.keys(config.variables ?? {}),
    ...config.treeNodeMappings.map((m) => m.name),
  ])
  const checkVars = (
    text: string | string[] | undefined,
    scope: ValidationIssue["scope"],
  ) => {
    if (!text) return
    for (const name of collectVariables(text)) {
      if (!knownVarNames.has(name)) {
        issues.push({
          level: "error",
          scope,
          message: `Unknown variable "{{${name}}}"`,
        })
      }
    }
  }
  for (const step of config.steps) {
    checkVars(step.text, { kind: "step", stepId: step.id, field: "text" })
    if (step.attachTo) {
      checkVars(step.attachTo.element, {
        kind: "step",
        stepId: step.id,
        field: "attachTo",
      })
    }
    if (step.advanceOn) {
      checkVars(step.advanceOn.selector, {
        kind: "step",
        stepId: step.id,
        field: "advanceOn",
      })
    }
  }
  for (const msg of config.messages) {
    checkVars(msg.description, {
      kind: "message",
      messageId: msg.id,
      field: "description",
    })
  }

  // 3. Tree mappings resolve to exactly one node
  for (const mapping of config.treeNodeMappings) {
    if (!mapping.name) {
      issues.push({
        level: "error",
        scope: { kind: "mapping", mappingName: mapping.name || "(blank)" },
        message: "Mapping name is required",
      })
    }
    if (!mapping.nodePath || mapping.nodePath.length === 0) {
      issues.push({
        level: "error",
        scope: { kind: "mapping", mappingName: mapping.name },
        message: "Mapping path is empty",
      })
      continue
    }
    if (categories.length === 0) {
      // skip — sim data not loaded yet
      continue
    }
    const resolved = resolveTreeNodeId(categories, mapping.nodePath)
    if (resolved === null) {
      issues.push({
        level: "error",
        scope: { kind: "mapping", mappingName: mapping.name },
        message: `Path does not resolve to any node: ${mapping.nodePath.join(" → ")}`,
      })
    }
  }

  // 4. Message injectAfterStep references real step IDs
  for (const msg of config.messages) {
    if (!msg.id) {
      issues.push({
        level: "error",
        scope: { kind: "message", messageId: msg.id || "(blank)", field: "id" },
        message: "Message ID is required",
      })
    }
    if (!msg.injectAfterStep) {
      issues.push({
        level: "error",
        scope: {
          kind: "message",
          messageId: msg.id,
          field: "injectAfterStep",
        },
        message: "Message must specify a step to inject after",
      })
      continue
    }
    if (!stepIds.has(msg.injectAfterStep)) {
      issues.push({
        level: "error",
        scope: {
          kind: "message",
          messageId: msg.id,
          field: "injectAfterStep",
        },
        message: `injectAfterStep references unknown step "${msg.injectAfterStep}"`,
      })
    }
  }

  // 5. attachTo/advanceOn non-empty when step needs them; warn for unknown selectors
  for (const step of config.steps) {
    if (step.attachTo) {
      if (!step.attachTo.element) {
        issues.push({
          level: "error",
          scope: { kind: "step", stepId: step.id, field: "attachTo" },
          message: "attachTo.element is required",
        })
      } else if (
        !step.attachTo.element.includes("{{") &&
        !isKnownStableSelector(step.attachTo.element)
      ) {
        issues.push({
          level: "warning",
          scope: { kind: "step", stepId: step.id, field: "attachTo" },
          message: `attachTo.element "${step.attachTo.element}" is not in the known selector catalog`,
        })
      }
    }
    if (step.advanceOn) {
      if (!step.advanceOn.selector) {
        issues.push({
          level: "error",
          scope: { kind: "step", stepId: step.id, field: "advanceOn" },
          message: "advanceOn.selector is required",
        })
      }
      if (!step.advanceOn.event) {
        issues.push({
          level: "warning",
          scope: { kind: "step", stepId: step.id, field: "advanceOn" },
          message: "advanceOn.event is empty",
        })
      }
    }
  }

  // 6. Unused variables (warning)
  const usedVars = new Set<string>()
  for (const step of config.steps) {
    for (const v of collectVariables(step.text)) usedVars.add(v)
    if (step.attachTo) for (const v of collectVariables(step.attachTo.element)) usedVars.add(v)
    if (step.advanceOn) for (const v of collectVariables(step.advanceOn.selector)) usedVars.add(v)
  }
  for (const msg of config.messages) {
    for (const v of collectVariables(msg.description)) usedVars.add(v)
  }
  for (const name of Object.keys(config.variables ?? {})) {
    if (!usedVars.has(name)) {
      issues.push({
        level: "warning",
        scope: { kind: "global", field: "variables" },
        message: `Variable "${name}" is defined but never used`,
      })
    }
  }

  return issues
}

export const errorCount = (issues: ValidationIssue[]): number =>
  issues.filter((i) => i.level === "error").length

export const warningCount = (issues: ValidationIssue[]): number =>
  issues.filter((i) => i.level === "warning").length
